/**
 * Testcase Stream Parser
 * Parses streaming testcase content and creates compartments
 */

class TestcaseParser {
  constructor() {
    this.state = {
      compartments: [],
      buffer: "",
      processingState: 'idle'
    };
  }

  // =================================================================
  // PUBLIC API (used by App.js)
  // =================================================================

  processStreamContent(params) {
    const { content, section } = params;

    if (section !== "testcases") {
      return {
        isSuccessful: false,
        content: []
      };
    }

    if (!content) {
      return {
        isSuccessful: false,
        content: []
      };
    }

    this.state.processingState = 'processing';
    this.state.buffer += content;

    try {
      this.parseTestcases(this.state.buffer);

      if (this.state.compartments.length > 0) {
        this.state.processingState = 'completed';
        return {
          isSuccessful: true,
          content: this.getValidCompartments()
        };
      }

      this.state.processingState = 'idle';
      return {
        isSuccessful: false,
        content: []
      };

    } catch (error) {
      console.error('Testcase Parser: Error processing content:', error);
      this.state.processingState = 'error';
      return {
        isSuccessful: false,
        content: []
      };
    }
  }

  resetState() {
    console.log('🔄 Testcase Parser: Resetting state');

    this.state = {
      compartments: [],
      buffer: "",
      processingState: 'idle'
    };
  }

  // =================================================================
  // PARSING LOGIC
  // =================================================================

  parseTestcases(buffer) {
    const beforeCount = this.state.compartments.length;
    const matches = [...buffer.matchAll(/\*\*Found Testcase:\*\*/g)];
    const currentCount = matches.length;

    if (currentCount > beforeCount) {
      // Create new compartments
      for (let i = beforeCount; i < currentCount; i++) {
        this.createCompartment(i + 1);
      }
      this.updateCompartmentContent(buffer);
    } else if (this.state.compartments.length > 0) {
      // Stream to last compartment
      this.streamToLastCompartment(buffer);
    }
  }

  createCompartment(number) {
    const compartment = {
      id: `testcase_${number}`,
      number: number,
      title: `Testcase ${number}`,
      content: "",
      isExpanded: true
    };

    this.state.compartments.push(compartment);
    console.log(`🆕 Testcase Parser: Compartment ${number} created`);
  }

  updateCompartmentContent(fullBuffer) {
    const sections = fullBuffer.split('**Found Testcase:**');

    sections.slice(1).forEach((section, index) => {
      if (index < this.state.compartments.length) {
        const existingCompartment = this.state.compartments[index];

        if (!this.isValidCompartment(existingCompartment)) {
          console.error(`❌ Invalid compartment at index ${index}:`, existingCompartment);
          return;
        }

        const lines = section.trim().split('\n');
        const title = (lines[0] && lines[0].trim()) || `Testcase ${index + 1}`;
        let cleanContent = lines.slice(1).join('\n').trim();
        cleanContent = cleanContent.replace(/ENDOFTESTCASES.*$/s, '').trim();

        const updatedCompartment = {
          ...existingCompartment,
          title: title,
          content: cleanContent
        };

        if (this.isValidCompartment(updatedCompartment)) {
          this.state.compartments[index] = updatedCompartment;
        }
      }
    });
  }

  streamToLastCompartment(buffer) {
    if (this.state.compartments.length === 0) return;

    const lastIndex = this.state.compartments.length - 1;
    const existingCompartment = this.state.compartments[lastIndex];

    if (!this.isValidCompartment(existingCompartment)) {
      console.error(`❌ Invalid last compartment at index ${lastIndex}:`, existingCompartment);
      return;
    }

    const sections = buffer.split('**Found Testcase:**');

    if (sections.length > lastIndex + 1) {
      const lastSection = sections[lastIndex + 1];
      const lines = lastSection.trim().split('\n');
      const title = (lines[0] && lines[0].trim()) || existingCompartment.title;
      let cleanContent = lines.slice(1).join('\n').trim();
      cleanContent = cleanContent.replace(/ENDOFTESTCASES.*$/s, '').trim();

      const updatedCompartment = {
        ...existingCompartment,
        title: title,
        content: cleanContent
      };

      if (this.isValidCompartment(updatedCompartment)) {
        this.state.compartments[lastIndex] = updatedCompartment;
      }
    }
  }

  // =================================================================
  // UTILITY & VALIDATION
  // =================================================================

  isValidCompartment(compartment) {
    return compartment &&
           typeof compartment.id === 'string' &&
           compartment.id.length > 0 &&
           typeof compartment.number === 'number' &&
           (typeof compartment.title === 'string' || compartment.title === undefined);
  }

  getValidCompartments() {
    return this.state.compartments.filter(compartment => {
      const isValid = this.isValidCompartment(compartment);
      if (!isValid) {
        console.warn('Invalid compartment filtered out:', compartment);
      }
      return isValid;
    });
  }
}

export default TestcaseParser;
